import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormLabel,
  Input
} from '@mui/material';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import { reservationSchema } from 'schema';
import { getApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PropTypes from 'prop-types';

import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  STAY_TYPE_NIGHT,
  STAY_TYPE_PARTIAL,
  BASE_CURRENCY,
  ID_CARD_TYPES,
  emptyCustomer
} from '../Room/ReserveRoomComponents/constants';
import { toCents, fromCents, formatAmount, subtractCents } from '../Room/ReserveRoomComponents/utils/money';
import {
  buildReservationFormData,
  submitReservation
} from '../Room/ReserveRoomComponents/services/reservationService';
import { lookupCustomerByPhone } from '../Room/ReserveRoomComponents/services/customerLookup';
import { extractReservationError, networkErrorMessage } from '../Room/ReserveRoomComponents/errorHandler';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Construye el datetime-local actual + 5 minutos en el formato YYYY-MM-DDTHH:mm.
 * Esto previene reservas hechas "en el pasado" cuando el formulario se abre y
 * el usuario tarda un momento en confirmar.
 */
const buildDefaultCheckIn = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 5);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Formatea una fecha como ISO UTC usando la zona horaria del navegador.
 *
 * Nota histórica: este flujo usa la TZ del navegador en vez de la TZ del
 * hotel (que sí usa ReserveRoom.js). Lo mantenemos así para preservar el
 * comportamiento previo; harmonizar ambos flujos a `hotelInputToUTC` es un
 * follow-up aparte.
 */
const browserToUTCString = (localDateStr) => new Date(localDateStr).toISOString();

/**
 * Construye el shape inicial del formulario. Las llaves coinciden con las que
 * espera el schema y el backend.
 */
const buildInitialValues = (today) => ({
  roomType: 'default',
  roomNo: 'default',
  stayType: STAY_TYPE_NIGHT,
  checkInDate: today,
  checkOutDate: '',
  paymentMethod: '',
  advanceAmount: '',
  totalAmount: '',
  paymentCurrency: '',
  exchangeRateUsed: '',
  amountInForeignCurrency: '',
  createdBy: '',
  customers: [emptyCustomer()]
});

/** Estilo común para los mensajes de error de Formik debajo de cada campo. */
const errorTextStyle = {
  color: '#f44336',
  fontSize: '0.75rem',
  marginTop: '4px',
  marginRight: '14px',
  marginLeft: '14px',
  fontFamily: 'Roboto,sans-serif'
};

/** Pequeño wrapper para los ErrorMessage repetidos del FieldArray. */
const FieldError = ({ name }) => (
  <div style={errorTextStyle}>
    <ErrorMessage name={name} />
  </div>
);
FieldError.propTypes = { name: PropTypes.string.isRequired };

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const AddReservation = ({ open, handleClose }) => {
  const today = buildDefaultCheckIn();
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || BASE_CURRENCY;

  const [roomData, setRoomData] = useState([]);
  const [roomNumbers, setRoomNumbers] = useState([]);
  const [existingCustomerData, setExistingCustomerData] = useState(null);
  const [amount, setAmount] = useState(0);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [initialValues, setInitialValues] = useState(() => buildInitialValues(today));

  // -- Carga inicial de habitaciones --------------------------------------
  useEffect(() => {
    let cancelled = false;
    const fetchRooms = async () => {
      try {
        const response = await getApi(`api/room/viewallrooms/${hotel?.hotelId}`);
        if (!cancelled) setRoomData(response?.data || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error cargando habitaciones', err);
      }
    };
    if (hotel?.hotelId) fetchRooms();
    return () => {
      cancelled = true;
    };
  }, [hotel?.hotelId]);

  // -- Carga inicial de tasas de cambio activas ---------------------------
  useEffect(() => {
    let cancelled = false;
    const fetchExchangeRates = async () => {
      try {
        const response = await getApi(`api/exchange/viewall/${hotel?.hotelId}`);
        const active = (response.data || []).filter((rate) => rate.isActive);
        if (!cancelled) setExchangeRates(active);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching exchange rates:', err);
      }
    };
    if (hotel?.hotelId) fetchExchangeRates();
    return () => {
      cancelled = true;
    };
  }, [hotel?.hotelId]);

  // -- Tipos de estancia (para resolver el precio nocturno desde pricing[]) --
  useEffect(() => {
    let cancelled = false;
    const fetchStayTypes = async () => {
      try {
        const response = await getApi(`api/hotel/stay-types/list/${hotel?.hotelId}`);
        if (response?.status === 200 && !cancelled) setStayTypes(response.data || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching stay types:', err);
      }
    };
    if (hotel?.hotelId) fetchStayTypes();
    return () => {
      cancelled = true;
    };
  }, [hotel?.hotelId]);

  /**
   * Precio nocturno (centavos) desde la Matriz de Tarifas (pricing[]).
   * El campo legacy room.amount solo se usa como último recurso: queda
   * desactualizado (la matriz no lo sincroniza) y las habitaciones nuevas lo
   * guardan en 0. Leerlo era la causa de cobrar un precio distinto al configurado.
   */
  const resolveNightlyPriceCents = (room) => {
    if (!room) return 0;
    const nightlyType = stayTypes?.find((t) => t.isNightly);
    const entry = nightlyType ? room.pricing?.find((p) => String(p.stayTypeId) === String(nightlyType._id)) : null;
    if (entry && entry.amount != null) return toCents(entry.amount);
    return toCents(room.amount || 0);
  };

  // -- Handlers de dominio -------------------------------------------------

  const handleRoomTypeChange = (selectedRoomType) => {
    const numbers = roomData.filter((r) => r.roomType === selectedRoomType).map((r) => r.roomNo);
    setRoomNumbers(numbers);
    setInitialValues((prev) => ({ ...prev, roomType: selectedRoomType, roomNo: 'default' }));
  };

  /**
   * Calcula amount cuando cambian roomNo, stayType o las fechas. Opera en
   * centavos para evitar el bug 10 -> 9 que el flujo legacy tenía.
   */
  const calculateAmount = (values, setFieldValue) => {
    const selectedRoom = roomData?.find((room) => room.roomNo === values.roomNo);
    if (!selectedRoom) return;

    if (values.stayType === STAY_TYPE_PARTIAL) {
      setAmount(selectedRoom.partialAmount || 0);

      // Partial = 3 horas fijas. Calculamos el check-out automáticamente.
      const checkIn = new Date(values.checkInDate);
      const checkOut = new Date(checkIn.getTime() + 3 * 60 * 60 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const formatted = `${checkOut.getFullYear()}-${pad(checkOut.getMonth() + 1)}-${pad(checkOut.getDate())}T${pad(checkOut.getHours())}:${pad(checkOut.getMinutes())}`;
      setFieldValue && setFieldValue('checkOutDate', formatted);
      return;
    }

    // Nightly: cobra precio * número de noches calculadas desde fechas.
    const checkIn = new Date(values.checkInDate);
    const checkOut = new Date(values.checkOutDate);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 3600 * 24));
    // Precio desde la matriz (pricing[]), con el legacy como último recurso.
    const priceCents = resolveNightlyPriceCents(selectedRoom);
    const multiplier = nights === 0 || Number.isNaN(nights) ? 1 : nights;
    setAmount(fromCents(multiplier * priceCents));
  };

  /**
   * Único punto de escritura para advanceAmount y totalAmount.
   *
   * Preservamos el string crudo del usuario para no romper la entrada de
   * decimales. El formato definitivo se aplica al enviar el FormData.
   */
  const handleTotalAmount = (advanceValue, setFieldValue) => {
    if (advanceValue === '' || advanceValue === null || advanceValue === undefined) {
      setFieldValue('advanceAmount', '');
      setFieldValue('totalAmount', formatAmount(toCents(amount)));
      return;
    }

    const advanceCents = toCents(advanceValue);
    if (advanceCents < 0) return;

    const amountCents = toCents(amount);
    if (advanceCents <= amountCents) {
      setFieldValue('advanceAmount', advanceValue);
      setFieldValue('totalAmount', formatAmount(subtractCents(amountCents, advanceCents)));
    } else {
      setFieldValue('advanceAmount', formatAmount(amountCents));
      setFieldValue('totalAmount', '0.00');
      toast.warning('El anticipo no puede ser mayor al total a pagar');
    }
  };

  // -- Submit --------------------------------------------------------------

  const AddData = async (values, resetForm) => {
    try {
      const cashRegisterId = localStorage.getItem('currentCashRegisterId');

      const formData = buildReservationFormData({
        values,
        hotel: { hotelId: hotel.hotelId, _id: hotel._id },
        cashRegisterId,
        toUTCString: browserToUTCString
      });

      const response = await submitReservation(formData);

      if (response.status === 200) {
        toast.success('Room successfully Reserved');
        handleClose();
        resetForm();
        setExistingCustomerData(null);
        return;
      }

      toast.error(extractReservationError(response));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error al crear reserva', err);
      toast.error(networkErrorMessage());
    }
  };

  // -- Lookup de cliente por teléfono -------------------------------------

  const onPhoneChange = async (index, raw, values, setFieldValue) => {
    const phoneNumber = raw.replace(/\D/g, '').slice(0, 10);
    setFieldValue(`customers.${index}.phoneNumber`, phoneNumber);

    if (phoneNumber.length !== 10) return;

    const data = await lookupCustomerByPhone(phoneNumber, hotel?.hotelId);
    if (!data) return;

    setExistingCustomerData(data);
    setFieldValue(`customers.${index}.firstName`, data.firstName || '');
    setFieldValue(`customers.${index}.lastName`, data.lastName || '');
    setFieldValue(`customers.${index}.email`, data.email || '');
    setFieldValue(`customers.${index}.idCardType`, data.idCardType || '');
    setFieldValue(`customers.${index}.idcardNumber`, data.idcardNumber || '');
    setFieldValue(`customers.${index}.address`, data.address || '');
    setFieldValue(`customers.${index}.idFile`, data.idFile || '');
  };

  // -- Reset al cerrar -----------------------------------------------------

  const handleCancel = () => {
    handleClose();
    setInitialValues(buildInitialValues(today));
    setAmount(0);
    setExistingCustomerData(null);
  };

  // ============================================================================

  return (
    <div>
      <style>{`.errorMessage { color: red; }`}</style>

      <Dialog open={open} aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description">
        <DialogTitle id="scroll-dialog-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Add Room Reservation</Typography>
          <Typography>
            <Button onClick={handleCancel} style={{ color: 'red' }}>
              Cancel
            </Button>
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Formik
            initialValues={initialValues}
            validationSchema={reservationSchema}
            onSubmit={(values, { resetForm }) => AddData(values, resetForm)}
            render={({ values, setFieldValue, errors, touched }) => (
              <Form encType="multipart/form-data">
                <Typography style={{ marginBottom: '15px', marginTop: '15px' }} variant="h6">
                  Room Information
                </Typography>

                <Grid container rowSpacing={3} columnSpacing={{ xs: 0, sm: 2 }}>
                  <Grid item xs={12} sm={12} md={6}>
                    <FormLabel>Room Type</FormLabel>
                    <Select
                      id="roomType"
                      name="roomType"
                      size="small"
                      fullWidth
                      value={values.roomType}
                      onChange={(e) => {
                        setFieldValue('roomType', e.target.value);
                        handleRoomTypeChange(e.target.value);
                      }}
                      error={errors.roomType && Boolean(errors.roomType)}
                    >
                      <MenuItem value="default" disabled>
                        Select Room Type
                      </MenuItem>
                      {roomData
                        ?.filter((room, idx, self) => self.findIndex((r) => r.roomType === room.roomType) === idx)
                        .map((room, idx) => (
                          <MenuItem key={idx} value={room?.roomType}>
                            {room?.roomType}
                          </MenuItem>
                        ))}
                    </Select>
                    <ErrorMessage name="roomType" />
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}>
                    <FormLabel>Room Number</FormLabel>
                    <Select
                      id="roomNo"
                      name="roomNo"
                      size="small"
                      fullWidth
                      value={values.roomNo}
                      onChange={(e) => setFieldValue('roomNo', e.target.value)}
                      error={errors.roomNo && Boolean(errors.roomNo)}
                    >
                      <MenuItem value="default" disabled>
                        Select Room Number
                      </MenuItem>
                      {roomNumbers.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                    <ErrorMessage name="roomNo" />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Stay Type</FormLabel>
                    <Select
                      id="stayType"
                      name="stayType"
                      value={values.stayType}
                      onChange={(e) => {
                        const next = e.target.value;
                        setFieldValue('stayType', next);
                        calculateAmount({ ...values, stayType: next }, setFieldValue);
                      }}
                      fullWidth
                    >
                      <MenuItem value={STAY_TYPE_NIGHT}>Per Night</MenuItem>
                      <MenuItem value={STAY_TYPE_PARTIAL}>Partial (3 Hours)</MenuItem>
                    </Select>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Check-In Date</FormLabel>
                    <TextField
                      id="checkInDate"
                      name="checkInDate"
                      size="small"
                      type="datetime-local"
                      fullWidth
                      value={values.checkInDate}
                      onChange={(e) => setFieldValue('checkInDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      error={errors.checkInDate && Boolean(errors.checkInDate)}
                    />
                    <ErrorMessage name="checkInDate" className="errorMessage" />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Check-Out Date</FormLabel>
                    <TextField
                      id="checkOutDate"
                      name="checkOutDate"
                      size="small"
                      type="datetime-local"
                      fullWidth
                      value={values.checkOutDate}
                      onChange={(e) => {
                        const next = e.target.value;
                        setFieldValue('checkOutDate', next);
                        calculateAmount({ ...values, checkOutDate: next }, setFieldValue);
                      }}
                      disabled={values.stayType === STAY_TYPE_PARTIAL}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: values.checkInDate }}
                      error={errors.checkOutDate && Boolean(errors.checkOutDate)}
                      helperText={touched.checkOutDate && errors.checkOutDate}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Amount</FormLabel>
                    <TextField
                      id="amount"
                      name="amount"
                      size="small"
                      type="number"
                      fullWidth
                      value={amount}
                      disabled
                      error={touched.amount && Boolean(errors.amount)}
                      helperText={touched.amount && errors.amount}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Payment Method</FormLabel>
                    <Select
                      id="paymentMethod"
                      name="paymentMethod"
                      value={values.paymentMethod || ''}
                      onChange={(e) => setFieldValue('paymentMethod', e.target.value)}
                      fullWidth
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select payment method
                      </MenuItem>
                      {Object.values(PAYMENT_METHODS).map((method) => (
                        <MenuItem key={method} value={method}>
                          {PAYMENT_METHOD_LABELS[method]}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>¿Pago en moneda extranjera?</FormLabel>
                    <Select
                      value={values.paymentCurrency || BASE_CURRENCY}
                      onChange={(e) => {
                        const selectedCurrency = e.target.value;
                        const selectedRate =
                          exchangeRates.find((rate) => rate.abbreviation === selectedCurrency)?.rate || 1;
                        const converted = (fromCents(toCents(amount)) * selectedRate).toFixed(2);

                        setFieldValue('paymentCurrency', selectedCurrency);
                        setFieldValue('exchangeRateUsed', selectedRate);
                        setFieldValue('amountInForeignCurrency', converted);
                      }}
                      fullWidth
                    >
                      <MenuItem value={BASE_CURRENCY}>{BASE_CURRENCY} (Base)</MenuItem>
                      {exchangeRates.map((rate) => (
                        <MenuItem key={rate._id} value={rate.abbreviation}>
                          {rate.abbreviation} - {rate.currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {values.paymentCurrency && values.paymentCurrency !== BASE_CURRENCY && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormLabel>Tasa de cambio utilizada</FormLabel>
                        <TextField value={values.exchangeRateUsed || ''} disabled fullWidth />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormLabel>Total en {values.paymentCurrency}</FormLabel>
                        <TextField value={values.amountInForeignCurrency || ''} disabled fullWidth />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Advance Amount</FormLabel>
                    <TextField
                      id="advanceAmount"
                      name="advanceAmount"
                      size="small"
                      type="number"
                      fullWidth
                      placeholder="Enter advance amount"
                      value={values.advanceAmount}
                      onChange={(e) => handleTotalAmount(e.target.value, setFieldValue)}
                      inputProps={{ step: '0.01', min: 0 }}
                      error={touched.advanceAmount && Boolean(errors.advanceAmount)}
                      helperText={touched.advanceAmount && errors.advanceAmount}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormLabel>Total Amount</FormLabel>
                    <TextField
                      id="totalAmount"
                      name="totalAmount"
                      size="small"
                      type="text"
                      disabled
                      fullWidth
                      value={`${currency} ${formatAmount(subtractCents(toCents(amount), toCents(values.advanceAmount)))}`}
                      error={touched.totalAmount && Boolean(errors.totalAmount)}
                      helperText={touched.totalAmount && errors.totalAmount}
                    />
                  </Grid>
                  <ErrorMessage name="totalAmount" style={{ color: 'red' }} />
                </Grid>

                <FieldArray
                  name="customers"
                  render={(arrayHelpers) => (
                    <div>
                      {values.customers.map((customer, index) => (
                        <CustomerSection
                          key={index}
                          index={index}
                          values={values}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          existingCustomerData={existingCustomerData}
                          onPhoneChange={(raw) => onPhoneChange(index, raw, values, setFieldValue)}
                          onAdd={() => arrayHelpers.insert(index + 1, emptyCustomer())}
                          onRemove={() => arrayHelpers.remove(index)}
                        />
                      ))}
                    </div>
                  )}
                />

                <DialogActions>
                  <Button type="submit" variant="contained" color="primary">
                    Add Reservation
                  </Button>
                </DialogActions>
              </Form>
            )}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

AddReservation.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
};

// ---------------------------------------------------------------------------
// CustomerSection — extraído del FieldArray para que el render principal
// sea legible. Solo presentación, sin lógica de negocio.
// ---------------------------------------------------------------------------

const CustomerSection = ({
  index,
  values,
  errors,
  setFieldValue,
  existingCustomerData,
  onPhoneChange,
  onAdd,
  onRemove
}) => {
  const customer = values.customers[index];
  const errorAt = (field) =>
    errors.customers &&
    errors.customers.length > index &&
    errors.customers[index] &&
    Boolean(errors.customers[index][field]);

  return (
    <div>
      <Typography style={{ marginBottom: '15px', marginTop: '15px' }} variant="h6">
        Customer Information
      </Typography>

      <Grid container rowSpacing={3} columnSpacing={{ xs: 0, sm: 2 }}>
        <Grid item xs={12} sm={6}>
          <FormLabel>Phone Number</FormLabel>
          <TextField
            id={`customers.${index}.phoneNumber`}
            name={`customers.${index}.phoneNumber`}
            size="small"
            type="text"
            fullWidth
            value={customer.phoneNumber}
            placeholder="Enter Phone Number"
            onChange={(e) => onPhoneChange(e.target.value)}
            error={errorAt('phoneNumber')}
          />
          <FieldError name={`customers.${index}.phoneNumber`} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormLabel>First Name</FormLabel>
          <TextField
            id={`customers.${index}.firstName`}
            name={`customers.${index}.firstName`}
            size="small"
            fullWidth
            placeholder="Enter First Name"
            value={customer.firstName || ''}
            onChange={(e) => setFieldValue(`customers.${index}.firstName`, e.target.value)}
            error={errorAt('firstName')}
          />
          <FieldError name={`customers.${index}.firstName`} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormLabel>Last Name</FormLabel>
          <TextField
            id={`customers.${index}.lastName`}
            name={`customers.${index}.lastName`}
            size="small"
            fullWidth
            placeholder="Enter Last Name"
            value={customer.lastName || ''}
            onChange={(e) => setFieldValue(`customers.${index}.lastName`, e.target.value)}
            error={errorAt('lastName')}
          />
          <FieldError name={`customers.${index}.lastName`} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormLabel>Email</FormLabel>
          <TextField
            id={`customers.${index}.email`}
            name={`customers.${index}.email`}
            size="small"
            fullWidth
            placeholder="Enter Email"
            value={customer.email || ''}
            onChange={(e) => setFieldValue(`customers.${index}.email`, e.target.value)}
            error={errorAt('email')}
          />
          <FieldError name={`customers.${index}.email`} />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <FormLabel>Id card Type</FormLabel>
          <Select
            id={`customers.${index}.idCardType`}
            name={`customers.${index}.idCardType`}
            size="small"
            fullWidth
            value={customer.idCardType || ''}
            onChange={(e) => setFieldValue(`customers.${index}.idCardType`, e.target.value)}
            error={errorAt('idCardType')}
          >
            <MenuItem value="default" disabled>
              Select Id Card Type
            </MenuItem>
            <MenuItem value={ID_CARD_TYPES.ID}>Personal Id</MenuItem>
            <MenuItem value={ID_CARD_TYPES.PASSPORT}>Passport</MenuItem>
            <MenuItem value={ID_CARD_TYPES.RIF}>Rif</MenuItem>
            <MenuItem value={ID_CARD_TYPES.DRIVING_LICENSE}>Driving License</MenuItem>
          </Select>
          <FieldError name={`customers.${index}.idCardType`} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormLabel>Id Card Number</FormLabel>
          <TextField
            id={`customers.${index}.idcardNumber`}
            name={`customers.${index}.idcardNumber`}
            size="small"
            type="text"
            fullWidth
            placeholder="Enter card number"
            value={customer.idcardNumber || ''}
            onChange={(e) => setFieldValue(`customers.${index}.idcardNumber`, e.target.value)}
            error={errorAt('idcardNumber')}
          />
          <FieldError name={`customers.${index}.idcardNumber`} />
        </Grid>

        {existingCustomerData?.idFile ? (
          <Grid item xs={12} sm={6}>
            <FormLabel>Id Proof</FormLabel>
            <Typography style={{ color: 'black', marginTop: '7px' }}>
              <a
                href={customer.idFile}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <Button startIcon={<VisibilityIcon />} variant="contained" color="primary">
                  View ID Proof
                </Button>
              </a>
            </Typography>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6}>
            <FormLabel>Upload ID Card File</FormLabel>
            <Input
              id={`customers.${index}.idFile`}
              name={`customers.${index}.idFile`}
              type="file"
              onChange={(event) => setFieldValue(`customers.${index}.idFile`, event.currentTarget.files[0])}
            />
            <FieldError name={`customers.${index}.idFile`} />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormLabel>Address</FormLabel>
          <TextField
            id={`customers.${index}.address`}
            name={`customers.${index}.address`}
            size="small"
            fullWidth
            placeholder="Enter Physical Address"
            value={customer.address || ''}
            onChange={(e) => setFieldValue(`customers.${index}.address`, e.target.value)}
            error={errorAt('address')}
          />
          <FieldError name={`customers.${index}.address`} />
        </Grid>
      </Grid>

      {index !== 0 && (
        <Button type="button" variant="outlined" sx={{ margin: '10px 0px' }} onClick={onRemove}>
          - Remove
        </Button>
      )}
      {index === 0 && (
        <Button type="button" variant="outlined" sx={{ margin: '10px 0px' }} onClick={onAdd}>
          + Add Person
        </Button>
      )}
    </div>
  );
};

CustomerSection.propTypes = {
  index: PropTypes.number.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  existingCustomerData: PropTypes.object,
  onPhoneChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};

export default AddReservation;
